import React, { useEffect, useRef, useState } from "react";

type RangeKey =
  | "all"
  | "today"
  | "yesterday"
  | "thisWeek"
  | "lastWeek"
  | "thisMonth"
  | "lastMonth"
  | "custom"
  | "specific";

export interface DateRangeValue {
  startDate: string | null;
  endDate: string | null;
}

interface OptionItem {
  key: RangeKey;
  label: string;
}

export interface DateRangeFilterDropdownProps {
  onChange?: (value: DateRangeValue, key: RangeKey) => void;
  options?: OptionItem[];
  align?: "left" | "right" | "top" | "bottom";
  direction?: "up" | "down";
  verticalAlign?: "top" | "bottom";
  buttonClassName?: string;
  dropdownClassName?: string;
  containerClassName?: string;
  containerStyle?: React.CSSProperties;
  buttonStyle?: React.CSSProperties;
  dropdownStyle?: React.CSSProperties;
}

export type Props = DateRangeFilterDropdownProps;

const toYMD = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

const toDMY = (str: string | null) => {
  if (!str) return "";
  const d = new Date(str);
  return `${String(d.getDate()).padStart(2, "0")}-${String(
    d.getMonth() + 1
  ).padStart(2, "0")}-${d.getFullYear()}`;
};

const DEFAULT_OPTIONS: OptionItem[] = [
  { key: "all", label: "All Data" },
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "thisWeek", label: "This Week" },
  { key: "lastWeek", label: "Last Week" },
  { key: "thisMonth", label: "This Month" },
  { key: "lastMonth", label: "Last Month" },
  { key: "custom", label: "Range Date" },
  { key: "specific", label: "Specific Date" },
];

const DateRangeFilterDropdown: React.FC<DateRangeFilterDropdownProps> = ({
  onChange,
  options,
  align = "left",
  direction,
  verticalAlign,
  buttonClassName = "btn btn-outline-primary dropdown-toggle bg-white",
  dropdownClassName = "",
  containerClassName = "",
  containerStyle,
  buttonStyle,
  dropdownStyle,
}) => {
  const opts = options?.length ? options : DEFAULT_OPTIONS;

  const [isOpen, setIsOpen] = useState(false);
  const [active, setActive] = useState<RangeKey>("all");
  const [customStart, setCustomStart] = useState<string | null>(null);
  const [customEnd, setCustomEnd] = useState<string | null>(null);
  const [specificDate, setSpecificDate] = useState<string | null>(null);
  const [isCustomApplied, setIsCustomApplied] = useState(false);

  const [label, setLabel] = useState<string>(
    opts.find((o) => o.key === "all")?.label ?? "All Data"
  );

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLUListElement | null>(null);
  const [menuWidth, setMenuWidth] = useState(180);

  useEffect(() => {
    if (isOpen && menuRef.current) {
      setMenuWidth(menuRef.current.offsetWidth);
    }
  }, [isOpen, active]);

  // tutup kalau klik di luar
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleSelect = (key: RangeKey) => {
    // reset custom kalau pindah ke opsi lain
    if (key !== "custom") {
      setCustomStart(null);
      setCustomEnd(null);
      setIsCustomApplied(false);
    }
    if (key !== "specific") {
      setSpecificDate(null);
    }

    setActive(key);

    if (key !== "custom" && key !== "specific") {
      const found = opts.find((o) => o.key === key)?.label ?? "";
      setLabel(found || "—");
      setIsOpen(false);

      // hitung langsung value sesuai key
      let value: DateRangeValue = { startDate: null, endDate: null };
      const now = new Date();

      if (key === "all") value = { startDate: null, endDate: null };
      else if (key === "yesterday") {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        value = { startDate: toYMD(yesterday), endDate: toYMD(yesterday) };
      } else if (key === "today")
        value = { startDate: toYMD(now), endDate: toYMD(now) };
      else if (key === "thisWeek") {
        const diff = (now.getDay() + 6) % 7;
        const mon = new Date(now);
        mon.setDate(now.getDate() - diff);
        const sun = new Date(mon);
        sun.setDate(mon.getDate() + 6);
        value = { startDate: toYMD(mon), endDate: toYMD(sun) };
      } else if (key === "lastWeek") {
        const d = new Date(now);
        d.setDate(d.getDate() - 7);
        const diff = (d.getDay() + 6) % 7;
        const mon = new Date(d);
        mon.setDate(d.getDate() - diff);
        const sun = new Date(mon);
        sun.setDate(mon.getDate() + 6);
        value = { startDate: toYMD(mon), endDate: toYMD(sun) };
      } else if (key === "thisMonth") {
        const first = new Date(now.getFullYear(), now.getMonth(), 1);
        const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        value = { startDate: toYMD(first), endDate: toYMD(last) };
      } else if (key === "lastMonth") {
        const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const first = new Date(prev.getFullYear(), prev.getMonth(), 1);
        const last = new Date(prev.getFullYear(), prev.getMonth() + 1, 0);
        value = { startDate: toYMD(first), endDate: toYMD(last) };
      }

      onChange?.(value, key);
    }
  };

  const handleApply = () => {
    let lbl = "Custom";
    if (!customStart && !customEnd) lbl = "Custom";
    else if (!customStart && customEnd) lbl = `Then - ${toDMY(customEnd)}`;
    else if (customStart && !customEnd) lbl = `${toDMY(customStart)} - Now`;
    else lbl = `${toDMY(customStart)} - ${toDMY(customEnd)}`;

    setLabel(lbl);
    setIsCustomApplied(true);
    onChange?.({ startDate: customStart, endDate: customEnd }, "custom");
    setIsOpen(false);
  };

  const handleApplySpecific = () => {
    if (!specificDate) return;
    setLabel(toDMY(specificDate));
    onChange?.({ startDate: specificDate, endDate: specificDate }, "specific");
    setIsOpen(false);
  };

  const isTop = align === "top" || verticalAlign === "top" || direction === "up";
  const isRight = align === "right";
  const alignmentClass = isRight ? "end-0 dropdown-menu-end" : "start-0";
  const currentMenuWidth = menuRef.current?.offsetWidth || menuWidth || 180;

  const computedDropdownStyle: React.CSSProperties = {
    minWidth: 180,
    zIndex: 1050,
    left: isRight ? "auto" : 0,
    right: isRight ? 0 : "auto",
    ...(isTop
      ? {
        bottom: "100%",
        top: "auto",
        marginBottom: "0.25rem",
        marginTop: 0,
        transformOrigin: isRight ? "bottom right" : "bottom left",
      }
      : {
        transformOrigin: isRight ? "top right" : "top left",
      }),
    ...dropdownStyle,
  };

  const subPanelStyle: React.CSSProperties = {
    top: isTop ? "auto" : 48,
    bottom: isTop ? "100%" : "auto",
    left: isRight ? "auto" : currentMenuWidth,
    right: isRight ? currentMenuWidth : "auto",
    zIndex: 1050,
    minWidth: 260,
    transformOrigin: isRight
      ? isTop ? "bottom right" : "top right"
      : isTop ? "bottom left" : "top left",
  };

  return (
    <div
      ref={wrapperRef}
      className={`dropdown position-relative d-inline-block ${containerClassName}`}
      style={containerStyle}
    >
      <button
        type="button"
        className={buttonClassName}
        style={buttonStyle}
        onClick={() => setIsOpen((s) => !s)}
      >
        {label}
      </button>

      {isOpen && (
        <ul
          ref={menuRef}
          className={`dropdown-menu show animate-pop-in ${alignmentClass} ${dropdownClassName}`}
          style={computedDropdownStyle}
        >
          {opts.map((o) => (
            <li key={o.key}>
              <button
                type="button"
                className={`dropdown-item ${active === o.key ? "active" : ""}`}
                onClick={() => handleSelect(o.key)}
              >
                {o.label}
              </button>
            </li>
          ))}
        </ul>
      )}

      {isOpen && active === "custom" && (
        <div
          className="card shadow position-absolute p-3 animate-pop-in"
          style={subPanelStyle}
        >
          <div className="mb-2">
            <label className="form-label">Start Date</label>
            <input
              type="date"
              className="form-control"
              value={customStart ?? ""}
              onChange={(e) => setCustomStart(e.target.value || null)}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">End Date</label>
            <input
              type="date"
              className="form-control"
              value={customEnd ?? ""}
              min={customStart || undefined}
              onChange={(e) => setCustomEnd(e.target.value || null)}
            />
          </div>
          <div className="text-end">
            <button type="button" className="btn btn-sm btn-primary" onClick={handleApply}>
              Apply
            </button>
          </div>
        </div>
      )}

      {isOpen && active === "specific" && (
        <div
          className="card shadow position-absolute p-3 animate-pop-in"
          style={subPanelStyle}
        >
          <div className="mb-2">
            <label className="form-label">Date</label>
            <input
              type="date"
              className="form-control"
              value={specificDate ?? ""}
              onChange={(e) => setSpecificDate(e.target.value || null)}
            />
          </div>
          <div className="text-end">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={!specificDate}
              onClick={handleApplySpecific}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilterDropdown;